import { get, has, includes, isEmpty, startCase } from "lodash";
import flagsmith from "@flagsmith/flagsmith";
import { PROD_AUTH0_DOMAINS } from "src/config/auth0Tenants";
import { isEnvTrue } from "src/services/env";

/**
 * TODO: We should have good typing for something as important as auth.
 *  This is just a start to it and may be incomplete.
 *
 * @typedef {Object} Auth0UserMetaDataTenant
 * @prop {string} name
 * @prop {string} tenantId
 * @prop {string} tenantUrl
 *
 * @typedef {Object} Auth0UserMetaData
 * @prop {string} operatorId
 * @prop {string} anonymousId
 * @prop {string} redashName1
 * @prop {string} redashName2
 * @prop {string[]} groups
 * @prop {string[]} permissions
 * @prop {string[]} roles
 * @prop {string[]} workspaceRoles
 * @prop {{tenants: Auth0UserMetaDataTenant[]}} tenantList
 *
 * @typedef {Object} Auth0User
 * @prop {string} email
 * @prop {boolean} email_verified
 * @prop {string} name
 * @prop {string} nickname
 * @prop {string} sub
 * @prop {Date} updated_at
 * @prop {string} [auth0Namespace]/created_at
 * @prop {string} [auth0Namespace]/email
 * @prop {string} [auth0Namespace]/preferred_name
 * @prop {Auth0UserMetaData} [auth0Namespace]/user_metadata
 *
 * @typedef {Object} AuthStub
 * @prop {string} [preferredUsername] Auth0 custom claim `preferred_name` only.
 *   SAML/SSO tenants (e.g. NatWest) may omit it. Not a menu-visibility gate.
 *   `given_name` / `family_name` are not copied here.
 * @prop {string} [username] Auth0 token `name`. Not used for the header label
 *   (often the email; `startCase` would mangle it).
 * @prop {string} [email]
 * @prop {Date} createdAt
 * @prop {number} loginsCount
 * @prop {string} operatorId `user_metadata.operatorId`, else token `sub`.
 * @prop {string} anonymousId
 * @prop {string} tenantId
 * @prop {string[]} groups
 * @prop {string[]} roles
 */

const INTERNAL_SUPPORT_EMAIL_DOMAINS = [
  "support.orchestrated.io",
  "support.teamform.co",
];

const INTERNAL_TEST_EMAIL_DOMAINS = [
  "test.orchestrated.io",
  "social.teamform.co",
];

export const UNKNOWN_USER = "UNKNOWN_USER";
export const POWER_ROLE = "POWER";
export const ADMIN_ROLE = "ADMIN";
export const DIRECTORY_ROLE = "DIRECTORY";
export const REPORTING_ROLE = "REPORTING";
// Generic Auth0 group "Reporting" (teamform-auth0 resolveRolePermissions) - distinct from REPORTING_ROLE
export const REPORTING_GENERIC_ROLE = "Reporting";
export const DEMAND_LEAD_POWER_SUB_ROLE = "DEMAND_LEAD";
export const SUPPLY_LEAD_POWER_SUB_ROLE = "SUPPLY_LEAD";
export const CUSTOM_POWER_SUB_ROLE = "CUSTOM";
export const POWER_SUB_ROLES = [
  DEMAND_LEAD_POWER_SUB_ROLE,
  SUPPLY_LEAD_POWER_SUB_ROLE,
  CUSTOM_POWER_SUB_ROLE,
];

export const ALLOWED_ROLES = {
  USER_REPORT: [ADMIN_ROLE],
  DESIGNS: [POWER_ROLE, REPORTING_ROLE, ADMIN_ROLE, DIRECTORY_ROLE],
};
// Matches tenant-specific Auth0 reporting groups (e.g. CBA_REPORTING, NAB_REPORTING)
const TENANT_REPORTING_PATTERN = /_REPORTING$/;
const EXACT_REPORTING_ROLES = [REPORTING_ROLE, REPORTING_GENERIC_ROLE];

export const auth0Namespace = "http://orchestrated.io/2020/03/identity/claims";

// TODO: Move relevant metadata from user to app metadata object
// export const auth0AppMetadataKey = `${auth0Namespace}/app_metadata`;
export const auth0UserMetadataKey = `${auth0Namespace}/user_metadata`;
export const originalSupportUserKey = "isOriginalSupportUser";

export const isProdAuth0Tenant = () =>
  PROD_AUTH0_DOMAINS.includes(process.env.REACT_APP_AUTH0_DOMAIN);

export const completeLogout = () => {
  void flagsmith.logout();
  window.localStorage.clear();
  window.location.replace(process.env.REACT_APP_DOMAIN);
};

export const getAuth0ConnectionFromHostname = (connection) => {
  return connection
    ? {
        connection,
      }
    : null;
};

export const getApiUrlFromAuth0 = (apiUrl, tenantId, overrideTenant) => {
  if (overrideTenant) {
    return `${overrideTenant.tenantUrl}/${overrideTenant.tenantId}`;
  }

  const baseUrl = apiUrl || process.env.REACT_APP_GRAPHQL_URL;
  return isEnvTrue(process.env.REACT_APP_IS_OFFLINE) || !tenantId
    ? `${process.env.REACT_APP_GRAPHQL_URL}`
    : `${baseUrl}/${tenantId}`;
};

const getFromCustomClaims = (user, key) => {
  return get(user, `${auth0Namespace}/${key}`);
};

const getAuth0UserMetadata = (user) => {
  return getFromCustomClaims(user, "user_metadata");
};

const getFromUserMetadata = (user, key, defaultValue) => {
  return get(getAuth0UserMetadata(user), key, defaultValue);
};

/**
 * @param {Auth0User|AuthStub} userOrStub
 * @returns {any}
 */
export const getFromUserOrAuth = (userOrStub, key, defaultValue) => {
  // get from user object (see createAuth for context)
  const stubField = get(userOrStub, key);
  if (stubField !== undefined) {
    return stubField;
  }

  // get from user metadata
  const userField = get(getAuth0UserMetadata(userOrStub), key);
  if (userField !== undefined) {
    return userField;
  }

  return defaultValue;
};

/**
 * @param {Auth0User|AuthStub} userOrStub
 * @returns {boolean}
 */
export const isInternalSupportUser = (userOrStub) => {
  const email = getFromUserOrAuth(userOrStub, "email", "");
  return INTERNAL_SUPPORT_EMAIL_DOMAINS.includes(email.split("@")[1]);
};

/**
 * @param {Auth0User|AuthStub} userOrStub
 * @returns {boolean}
 */
export const isSupportImpersonatingUser = (userOrStub) => {
  return Boolean(
    get(userOrStub, originalSupportUserKey) &&
    getFromUserOrAuth(userOrStub, "impersonation.email")
  );
};

/**
 * @param {Auth0User|AuthStub} userOrStub
 * @returns {boolean}
 */
export const isInternalTestUser = (userOrStub) => {
  const email = getFromUserOrAuth(userOrStub, "email", "");
  return INTERNAL_TEST_EMAIL_DOMAINS.includes(email.split("@")[1]);
};

/**
 * @param {Auth0User|AuthStub} userOrStub
 * @returns {boolean}
 */
export const isOnboardingUser = (userOrStub) => {
  return getFromUserOrAuth(userOrStub, "tenantStatus") === "ONBOARDING_START";
};

/**
 * @param {Auth0User|AuthStub} userOrStub
 * @returns {boolean}
 */
export const hasTenantPrompt = (userOrStub) => {
  return (
    Boolean(getFromUserOrAuth(userOrStub, "tenantList")) &&
    (isInternalSupportUser(userOrStub) || isInternalTestUser(userOrStub))
  );
};

/**
 * @param {Auth0User|AuthStub} userOrStub
 * @returns {boolean}
 */
export const hasAdminRole = (userOrStub) => {
  return getFromUserOrAuth(userOrStub, "roles", []).includes(ADMIN_ROLE);
};

/**
 * @param {Auth0User|AuthStub} userOrStub
 * @returns {boolean}
 */
export const hasPowerRole = (userOrStub) => {
  return getFromUserOrAuth(userOrStub, "roles", []).includes(POWER_ROLE);
};

/**
 * @param {Auth0User|AuthStub} userOrStub
 * @returns {boolean}
 */
export const hasDirectoryRole = (userOrStub) => {
  return getFromUserOrAuth(userOrStub, "roles", []).includes(DIRECTORY_ROLE);
};

/**
 * Returns the user's power sub-roles (e.g. DEMAND_LEAD, SUPPLY_LEAD, CUSTOM).
 * @param {Auth0User|AuthStub} userOrStub
 * @returns {string[]}
 */
export const getPowerSubRoles = (userOrStub) => {
  const powerSubRoles = getFromUserOrAuth(userOrStub, "powerSubRoles", []);
  return Array.isArray(powerSubRoles) ? powerSubRoles : [];
};

/**
 * @param {Auth0User|AuthStub} userOrStub
 * @returns {boolean}
 */
export const hasAdminOrPowerRole = (userOrStub) => {
  const roles = getFromUserOrAuth(userOrStub, "roles", []);
  return includes(roles, ADMIN_ROLE) || includes(roles, POWER_ROLE);
};

/**
 * Checks if user has a valid reporting role.
 * Matches exact roles (REPORTING, Reporting) or tenant-specific roles via
 * regex (e.g. ABC_REPORTING, XYZ_REPORTING).
 * @param {Auth0User|AuthStub} userOrStub
 * @returns {boolean}
 */
export const hasReportingRole = (userOrStub) => {
  const userRoles = getFromUserOrAuth(userOrStub, "roles", []);

  if (userRoles.some((role) => includes(EXACT_REPORTING_ROLES, role))) {
    return true;
  }

  return userRoles.some(
    (role) => typeof role === "string" && TENANT_REPORTING_PATTERN.test(role)
  );
};

/**
 * @param {Auth0User|AuthStub} user
 * @returns {Auth0User|AuthStub}
 */
export const getImpersonatedUser = (user) => {
  if (user && isInternalSupportUser(user)) {
    const impersonation = getFromUserMetadata(user, "impersonation");
    let roles = getFromUserMetadata(user, "roles");
    if (!isEmpty(impersonation?.roles)) {
      roles = impersonation.roles;
    }
    let powerSubRoles = getFromUserMetadata(user, "powerSubRoles", []);
    if (!Array.isArray(powerSubRoles)) {
      powerSubRoles = [];
    }
    if (has(impersonation, "powerSubRoles")) {
      powerSubRoles = Array.isArray(impersonation.powerSubRoles)
        ? impersonation.powerSubRoles
        : [];
    } else if (!isEmpty(impersonation?.roles)) {
      powerSubRoles = [];
    }

    let email = getFromUserOrAuth(user, "email", "");
    if (!isEmpty(impersonation?.email)) {
      email = impersonation.email;
    }

    const impersonatedUser = {
      ...user,
      email,
      [originalSupportUserKey]: true,
      [`${auth0Namespace}/email`]: email,
      [`${auth0Namespace}/user_metadata`]: {
        ...getAuth0UserMetadata(user),
        roles,
        powerSubRoles,
      },
    };
    return impersonatedUser;
  }
  return user;
};

/**
 * Map an Auth0 user profile to the app auth stub.
 *
 * `preferredUsername` is set only from the `preferred_name` custom claim.
 * Missing SAML name claims must not prevent logout: the header menu is gated
 * on `userMenuContent`, and the first-row label uses
 * {@link getHeaderUserDisplayName} with email / login-id fallbacks.
 *
 * @param {Auth0User} user
 * @returns {AuthStub}
 */
export const createAuth = (user) => ({
  preferredUsername: getFromCustomClaims(user, "preferred_name"),
  username: get(user, "name"),
  email: get(user, "email"),
  createdAt: getFromCustomClaims(user, "created_at"),
  loginsCount: getFromCustomClaims(user, "logins_count"),
  operatorId: getFromUserMetadata(user, "operatorId") || get(user, "sub"),
  anonymousId: getFromUserMetadata(user, "anonymousId"),
  tenantId: getFromUserMetadata(user, "tenantId"),
  groups: getFromUserMetadata(user, "groups"),
  roles: getFromUserMetadata(user, "roles", []),
});

/**
 * First-row label for the header user menu. Does **not** control whether
 * Logout appears — Header renders the avatar menu whenever `userMenuContent`
 * is passed (HELP-11104).
 *
 * Source order:
 * 1. `preferredUsername` — Auth0 `preferred_name`, start-cased like master
 * 2. `email` — Auth0 `email` (not token `name` / stub `username`)
 * 3. `id`, then `operatorId` — login id (`user_metadata.operatorId` or `sub`)
 *
 * TeamForm person `preferredName` / `firstName` are unused here; avatar and
 * initials still come from `userPerson` in AppNav.
 *
 * @param {object|null} [fields] Auth stub or a subset. `null` is empty.
 * @param {string} [fields.preferredUsername]
 * @param {string} [fields.email]
 * @param {string} [fields.id] AppNav passes `operatorId` as `id`.
 * @param {string} [fields.operatorId]
 * @returns {string|undefined} Trimmed label, or undefined if nothing usable.
 */
const nonEmptyString = (value) => {
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed || undefined;
};

export const getHeaderUserDisplayName = (fields = {}) => {
  const { preferredUsername, email, id, operatorId } = fields || {};
  return (
    nonEmptyString(startCase(preferredUsername)) ||
    nonEmptyString(email) ||
    nonEmptyString(id) ||
    nonEmptyString(operatorId)
  );
};

// =====================================================================
// Tenant-scoped custom role names
// =====================================================================
// The Auth0 token's user_metadata.groups array contains tenant-scoped role
// strings whose name encodes the tenant id. Centralised here so callers
// don't hardcode prefixes.
//
//   DTA-{tenantId}    Design Tenant Admin (tenant-wide)

export const TENANT_ROLE_RESOURCES = {
  DESIGN: "DESIGN",
};

const TENANT_ROLE_NAME_PREFIXES = {
  [TENANT_ROLE_RESOURCES.DESIGN]: {
    ADMIN: "DTA",
  },
};

/**
 * Curried factory for tenant-scoped role names.
 *
 * @example
 *   const customRoles = tenantRoleNamesFactory("fjak");
 *   const designRoles = customRoles(TENANT_ROLE_RESOURCES.DESIGN);
 *   // designRoles.ADMIN === "DTA-fjak"
 *
 * @param {string} tenantId
 * @returns {(resource: string) => Record<string, string>}
 */
export const tenantRoleNamesFactory = (tenantId) => (resource) => {
  const prefixes = TENANT_ROLE_NAME_PREFIXES[resource];
  if (!prefixes || !tenantId) {
    throw new Error("Tenant ID and resource type are required");
  }
  return Object.fromEntries(
    Object.entries(prefixes).map(([key, prefix]) => [
      key,
      `${prefix}-${tenantId}`,
    ])
  );
};

/**
 * Checks whether the user belongs to a given group (tenant-scoped role).
 *
 * Internal-support bypass is intentionally NOT applied here — apply at the
 * call site when desired so this helper stays a pure membership check.
 *
 * @param {Auth0User|AuthStub} userOrStub
 * @param {string} roleName
 * @returns {boolean}
 */
export const userHasGroupRole = (userOrStub, roleName) => {
  if (!roleName) {
    return false;
  }
  const groups = getFromUserOrAuth(userOrStub, "groups", []);
  return Array.isArray(groups) && groups.includes(roleName);
};

/**
 * @param {Auth0User|AuthStub} userOrStub
 * @param {string[]} roleNames
 * @returns {boolean}
 */
export const userHasAnyGroupRole = (userOrStub, roleNames = []) => {
  if (!roleNames.length) {
    return false;
  }
  const groups = getFromUserOrAuth(userOrStub, "groups", []);
  if (!Array.isArray(groups)) {
    return false;
  }
  return roleNames.some((r) => r && groups.includes(r));
};

/**
 * @param {{
 *   user: Auth0User,
 *   person: import('src/custom-prop-types/person').Person,
 *   userPerson: import('src/custom-prop-types/person').Person,
 * }} props
 * @returns {boolean}
 */
export const isOwnerOrAdmin = ({ user, person, userPerson }) => {
  if (hasAdminRole(user)) {
    return true;
  }

  if (!userPerson || !person) {
    return false;
  }

  return userPerson?.aggregateId === person?.aggregateId;
};
